import { FC, ReactNode, useMemo, useState } from 'react';
import { WithScope } from '@mews-commander/types';
import { SelectField } from '@mews-commander/form';
import { TextKey } from '@mews-commander/enums';
import { FormCheckboxField, LocalizedText, lt, useFormControls } from '@mews/framework';
import { canCurrentProfileOverrideRatePrice } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector, useGainsightCommanderEvents } from '@mews-commander/utils';
import { RateResult, RateType } from '@mews-commander/data-access';
import { createOption } from '@mews-commander/ui';
import { useEnterpriseRatesQuery } from '@mews-commander/core/entities';
import { FormLayout } from '../../FormLayout';
import { getRateFormInitialValues, getRateFormServiceId } from '../selectors';
import { useRoutingRuleChangesConfirmation } from '../../../../routingRulesNew/components/RoutingRuleChangeConfirmationPanel/useRoutingRuleChangesConfirmation';
import { RateFormKeys, RateFormValues } from './types';
import { useChangeRateMutation } from './useChangeRateMutation';

interface RateFormProps extends WithScope {
    reservationIds: string[];
    onSuccess?: () => void;
}

const getRateOptions = (serviceId: string | null) => (data: RateResult) => {
    const filteredRates = data.rates?.filter(
        ({ isEnabled, type, serviceId: fetchedRateServiceId }) =>
            isEnabled && type !== RateType.AvailabilityBlock && fetchedRateServiceId === serviceId
    );

    const ratesOptions = filteredRates?.map(({ name, id }) =>
        createOption({
            label: name,
            value: id,
        })
    );

    return ratesOptions;
};

const CHANGE_RATE_FORM = 'change-rate-form';

export const RateForm: FC<RateFormProps> = ({ scope, reservationIds, onSuccess }: RateFormProps) => {
    const initialValues = useCommanderSelector((state) => getRateFormInitialValues(state, { scope }));
    const serviceId = useCommanderSelector((state) => getRateFormServiceId(state, { scope }));
    const { rateId } = initialValues;

    const getRateOptionsWithServiceId = getRateOptions(serviceId);

    const { data: fetchedRatesOptions = [] } = useEnterpriseRatesQuery(getRateOptionsWithServiceId);

    const isRateIdValid = rateId && fetchedRatesOptions.find(({ value }) => value === rateId) !== undefined;
    const canOverrideRatePrice = useCommanderSelector((state) => canCurrentProfileOverrideRatePrice(state));
    const changeRateMutation = useChangeRateMutation(reservationIds);
    const [routingRuleChangesDialog, setRoutingRuleChangesDialog] = useState<ReactNode>(null);

    const defaultValues = useMemo(
        () => ({ ...initialValues, rateId: isRateIdValid ? rateId : '' }),
        [initialValues, isRateIdValid, rateId]
    );

    const handleSubmit = (values: RateFormValues) => {
        const result = validate();

        if (result) {
            setRoutingRuleChangesDialog(result.dialog);
            return;
        }

        changeRateMutation.mutate(values, { onSuccess });
    };

    const { renderFormContent, watch, reset } = useFormControls<RateFormValues>({
        onSubmit: handleSubmit,
        values: defaultValues as RateFormValues,
        dataTestForm: `${CHANGE_RATE_FORM}-${scope}`,
    });

    const currentRateId = watch(RateFormKeys.rateId);
    const rateIdChanged = defaultValues[RateFormKeys.rateId] !== currentRateId;

    const gainsightEvents = useGainsightCommanderEvents();

    const { validate, isLoading } = useRoutingRuleChangesConfirmation({
        reservationIds,
        rateId: currentRateId,
        enabled: rateIdChanged,
        scope,
        onSubmit: ({ changes }) => {
            const onInternalSuccess = () => {
                gainsightEvents.onApiSuccess({
                    context: 'BillingAutomationChanged',
                    data: {
                        changes,
                        origin: 'Rates',
                    },
                });

                onSuccess?.();
            };

            changeRateMutation.mutate(watch(), { onSuccess: onInternalSuccess });
            setRoutingRuleChangesDialog(null);
        },
        onCancel: () => {
            reset();
            setRoutingRuleChangesDialog(null);
        },
    });

    return (
        <>
            {renderFormContent(
                <FormLayout
                    title={lt('ChangeRate')}
                    loading={changeRateMutation.isPending}
                    submitButtonLoading={isLoading}
                >
                    <SelectField
                        name={RateFormKeys.rateId}
                        options={fetchedRatesOptions}
                        showEmptyValue={!isRateIdValid}
                    />
                    {canOverrideRatePrice && (
                        <FormCheckboxField
                            id={`${RateFormKeys.keepOldPrice}-${CHANGE_RATE_FORM}-${scope}`}
                            name={RateFormKeys.keepOldPrice}
                        >
                            <LocalizedText textKey={TextKey.KeepOldPrice} />
                        </FormCheckboxField>
                    )}
                </FormLayout>
            )}
            {routingRuleChangesDialog}
        </>
    );
};
